import { ArchiveIcon } from "@phosphor-icons/react/dist/csr/Archive";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { canManageReport } from "@/profitability/access";
import { useArchiveReport } from "@/profitability/report-query";
import { templateStatus } from "@/profitability/template-status";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { BuildQuestions } from "./build-questions";

/** The template list loading, failing, or not holding this template. */
export const TemplateLoadNotice = ({
  reports,
  found,
}: {
  /** The template's own query: it is found by id, archived or not. */
  reports: {
    isPending: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  found: boolean;
}) => (
  <>
    {reports.isPending && (
      <Text role="status" tone="muted">
        Loading agent…
      </Text>
    )}
    {reports.isError && (
      <Stack gap={3} align="start">
        <Text tone="destructive" role="alert">
          The agent could not load. {reportError(reports.error)} Try again.
        </Text>
        <Button variant="secondary" onClick={() => void reports.refetch()}>
          Try again
        </Button>
      </Stack>
    )}
    {reports.isSuccess && !found && (
      <Text tone="muted">
        This agent does not exist or belongs to another business. Open Job
        Costing to choose another agent.
      </Text>
    )}
  </>
);

/** Where the template's setup stands, above its tabs: its status and build. */
export const TemplateBuildNotice = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const status = templateStatus(report);
  const buildStatus = report.build_status;
  return (
    <>
      <TemplateArchivedNotice businessId={businessId} report={report} />
      {/* Where the agent stands whenever it is not simply ready: being set
          up, building, stuck, or holding a draft to review. */}
      {/* A published agent's draft has its own bar (`DraftChangesBar`). */}
      {status.label !== "Ready" && status.label !== "Changes to review" && (
        <Badge variant="secondary" color={status.color} className="self-start">
          {status.label}
        </Badge>
      )}
      {/* Before publishing, the builder below says these itself. */}
      {buildStatus === "building" && report.published && (
        <Text role="status" tone="muted">
          Volca is reading your files and building this agent. You can leave
          this page and come back.
        </Text>
      )}
      {buildStatus === "failed" && report.published && (
        <Text tone="destructive" role="alert">
          The build didn&apos;t finish. Tell Volca what to change in Edit agent,
          or use Rebuild in the actions menu to try again with the same files.
        </Text>
      )}
      {buildStatus === "needs_input" && canManageReport(report) && (
        <Stack className="max-w-3xl">
          <BuildQuestions
            key={report.build_questions.join("\n")}
            businessId={businessId}
            report={report}
          />
        </Stack>
      )}
    </>
  );
};

/**
 * An archived template keeps everything it made and can be read, but not run
 * until restored (RPT-008); its page says so, with the way back.
 */
export const TemplateArchivedNotice = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const restore = useArchiveReport(businessId, "restore");
  if (!report.archived_at) return null;
  return (
    <Alert color="information" className="max-w-3xl">
      <ArchiveIcon className="size-5" />
      <AlertTitle>This agent is archived</AlertTitle>
      <AlertDescription>
        <Stack align="start" gap={2}>
          <span>
            Its periods stay as they were, but it cannot run new ones until you
            restore it.
          </span>
          {report.access !== "view" && (
            <Button
              size="sm"
              variant="secondary"
              loading={restore.isPending}
              onClick={() =>
                restore.mutate(report.id, {
                  onSuccess: () => toast.success(`${report.name} is restored`),
                  onError: (error) =>
                    toast.error("The agent was not restored", {
                      description: reportError(error),
                    }),
                })
              }
            >
              Restore
            </Button>
          )}
        </Stack>
      </AlertDescription>
    </Alert>
  );
};
